<script setup lang="ts">
import SupportBanner from './SupportBanner.vue';
/**
 * The signed-in app shell: a Displaro slate (#0F172A) sidebar with the navigation, the organization switcher
 * and the account menu, and a light content area.
 *  - Desktop (lg+): the sidebar is always there and collapses to an icon rail; the choice is remembered
 *    per browser.
 *  - Below lg: a compact top bar with a menu button; the sidebar slides in over the content and closes on
 *    navigation, Esc or a tap outside.
 * Navigation, role checks, organization switching and sign-out behave exactly as before (App.vue Phase 3–8).
 */
import { usePlan } from '../stores/plan';
import ThemeSwitcher from './ThemeSwitcher.vue';
import { useHealthSummary } from '../utils/health';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { errorMessage } from '../api/http';
import { useAuthStore } from '../stores/auth.store';
import ToastHost from './ToastHost.vue';
import IssuePanelHost from './IssuePanelHost.vue';
import UploadTray from './UploadTray.vue';
import SyncTray from './SyncTray.vue';
import PublishIntentHost from './PublishIntentHost.vue';
import UserAvatar from './UserAvatar.vue';
import NotificationBell from './NotificationBell.vue';

const { t } = useI18n();
const plan = usePlan();
const auth = useAuthStore();
const router = useRouter();
const route = useRoute();
const org = computed(() => auth.activeOrganization);
const switching = ref(false);
const switchError = ref<string | null>(null);

const COLLAPSE_KEY = 'codot.sidebar.collapsed';
const collapsed = ref(read(COLLAPSE_KEY) === '1');
const mobileOpen = ref(false);

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
watch(collapsed, (v) => {
  try {
    localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0');
  } catch {
    /* private mode: not remembered */
  }
});
watch(
  () => route.fullPath,
  () => (mobileOpen.value = false),
);

type Icon = 'content' | 'monitoring' | 'apps' | 'feeds' | 'players' | 'groups' | 'analytics' | 'regions' | 'tags' | 'health' | 'alerts' | 'activity' | 'reports' | 'org' | 'attributes';
interface NavItem {
  to: { name: string };
  label: string;
  icon: Icon;
  /** Extra route names that count as "inside" this section. */
  also?: string[];
  /** Phase 16 Step 13: a count shown next to the label (open critical + warning issues). */
  badge?: { count: number; tone: 'critical' | 'warning' } | null;
  /** Phase 17.10: the page's feature needs the Advanced plan. */
  locked?: boolean;
}
/** Displaro's own grouping: what you make (Create), where it plays (Screens), how it's going (Insights). */
/** Access limited by group: organization-wide areas (tags, regions, alerts, data feeds) are closed; reports cover their groups. */
const limited = computed(() => !!org.value?.groupIds);
const NAV = computed<{ title: string; items: NavItem[] }[]>(() =>
  org.value
    ? [
        {
          title: t('nav.sectionCreate'),
          items: [
            {
              to: { name: 'dashboard' },
              label: t('nav.content'),
              icon: 'content',
              also: ['layouts', 'media', 'campaign', 'campaign-new', 'playlist', 'playlist-new'],
            },
            { to: { name: 'apps' }, label: t('nav.apps'), icon: 'apps', also: ['custom-app'] },
            ...(limited.value ? [] : [{ to: { name: 'data-feeds' }, label: t('nav.dataFeeds'), icon: 'feeds' as const, also: ['data-feed'], locked: !plan.has('dataFeeds') }]),
            ...(limited.value ? [] : [{ to: { name: 'tags' }, label: t('nav.tags'), icon: 'tags' as const }]),
          ],
        },
        {
          title: t('nav.sectionScreens'),
          items: [
            {
              to: { name: 'players' },
              label: t('nav.players'),
              icon: 'players',
              also: [
                'screen-detail',
                'screen-published',
                'screen-activity',
                'screen-resources',
                'screen-logs',
                'screen-live',
                'screen-content',
              ],
            },
            { to: { name: 'groups' }, label: t('nav.playerGroups'), icon: 'groups' },
            ...(limited.value ? [] : [{ to: { name: 'regions' }, label: t('nav.regions'), icon: 'regions' as const, locked: !plan.has('regions') }]),
          ],
        },
        {
          title: t('nav.sectionInsights'),
          items: [
            { to: { name: 'monitoring' }, label: t('nav.monitoring'), icon: 'monitoring', locked: !plan.has('monitoringWall') },
            { to: { name: 'health-check' }, label: t('nav.healthCheck'), icon: 'health', badge: healthBadge.value },
            ...(limited.value ? [] : [{ to: { name: 'alerts' }, label: t('nav.alerts'), icon: 'alerts' as const, also: ['alert-new', 'alert-edit'], locked: !plan.has('alerts') }]),
            { to: { name: 'analytics' }, label: t('nav.analytics'), icon: 'analytics' },
            { to: { name: 'activity-log' }, label: t('nav.activityLog'), icon: 'activity' },
            { to: { name: 'reports' }, label: t('nav.reports'), icon: 'reports' },
          ],
        },
        // Same rule as before: VIEWER and up (the route guard enforces it too).
        ...(auth.can('VIEWER')
          ? [
              {
                title: t('nav.sectionAccount'),
                items: [
                  { to: { name: 'organization-settings' }, label: t('nav.organization'), icon: 'org' as const },
                  { to: { name: 'custom-attributes' }, label: t('nav.customAttributes'), icon: 'attributes' as const },
                ],
              },
            ]
          : []),
      ]
    : [],
);
/** Phase 16 Step 13: open, unacknowledged critical + warning issues (polled every 60 s). */
const health = useHealthSummary();
const healthBadge = computed(() => {
  const h = health.value;
  if (!h || !(h.critical + h.warning)) return null;
  return { count: h.critical + h.warning, tone: h.critical ? ('critical' as const) : ('warning' as const) };
});
const isActive = (item: NavItem) => route.name === item.to.name || (item.also ?? []).includes(String(route.name));

const ICONS: Record<Icon, string> = {
  content: 'M4 4h5v5H4zM11 4h5v5h-5zM4 11h5v5H4zM11 11h5v5h-5z',
  players: 'M6.5 4.5v11l9-5.5z',
  feeds: 'M4 5h12v10H4zM4 8.5h12M4 12h12M9 5v10',
  monitoring: 'M3 4h14v9H3zM7 16h6M10 13v3M8 7.5l2.5 2L13 7',
  apps: 'M4 4h5v5H4zM11 4h5v5h-5zM4 11h5v5H4zM13.5 11v5M11 13.5h5',
  groups: 'M5 5v10M8.5 4.5v11l8-5.5z',
  analytics: 'M4 16h12M6 13V9M10 13V5M14 13v-6',
  tags: 'M3 3.5h6.2l7.3 7.3-5.7 5.7L3.5 9.2V3.5zM6.5 6.5h.01',
  regions: 'M10 17s-5-4.6-5-8.5a5 5 0 0 1 10 0C15 12.4 10 17 10 17zM10 10.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  health: 'M3 10h3l2-4 4 8 2-4h3',
  alerts: 'M10 3.5a4 4 0 0 0-4 4v3l-1.5 3h11L14 10.5v-3a4 4 0 0 0-4-4zM8.5 15.5a1.5 1.5 0 0 0 3 0',
  activity: 'M5 4.5h10M5 8.5h10M5 12.5h6M5 16.5h4',
  reports: 'M6 3.5h6l3 3v10H6zM12 3.5v3h3M8.5 13v-2.5M10.5 13V9M12.5 13v-1.5',
  attributes: 'M4 5.5h7l5 4.5-5 4.5H4zM7 10h.01',
  org: 'M4 16.5V5l6-2.5L16 5v11.5M8 16.5v-4h4v4M7.5 7.5h1M11.5 7.5h1M7.5 10h1M11.5 10h1',
};


async function onSwitch(event: Event): Promise<void> {
  const id = (event.target as HTMLSelectElement).value;
  if (id === org.value?.id) return;
  switching.value = true;
  switchError.value = null;
  try {
    await auth.switchOrganization(id);
    // Everything on screen belonged to the previous tenant → start fresh on the dashboard.
    await router.replace({ name: 'dashboard' });
  } catch (err) {
    switchError.value = errorMessage(err);
  } finally {
    switching.value = false;
  }
}

async function signOut(): Promise<void> {
  await auth.logout();
  await router.replace({ name: 'login' });
}

const onKey = (e: KeyboardEvent) => e.key === 'Escape' && (mobileOpen.value = false);
onMounted(() => document.addEventListener('keydown', onKey));
onBeforeUnmount(() => document.removeEventListener('keydown', onKey));
</script>

<template>
  <div class="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
    <a
      href="#main"
      class="sr-only z-50 rounded bg-white px-3 py-2 focus:not-sr-only focus:fixed focus:top-2 focus:start-2"
      >{{ t('nav.skip') }}</a
    >

    <!-- Mobile top bar -->
    <header class="sticky top-0 z-30 flex h-14 items-center gap-3 bg-[#0F172A] px-4 text-white lg:hidden">
      <button
        type="button"
        class="-ms-1 rounded-md p-2 text-slate-300 hover:bg-white/10 hover:text-white"
        :aria-label="t('nav.open')"
        :aria-expanded="mobileOpen"
        aria-controls="app-sidebar"
        @click="mobileOpen = true"
      >
        <svg
          viewBox="0 0 20 20"
          class="h-5 w-5"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          aria-hidden="true"
        >
          <path d="M3 6h14M3 10h14M3 14h14" stroke-linecap="round" />
        </svg>
      </button>
      <RouterLink to="/" class="wordmark text-xl">Displaro</RouterLink>
      <span v-if="org" class="ms-auto truncate text-sm text-slate-400">{{ org.name }}</span>
      <NotificationBell placement="header" />
    </header>

    <!-- Mobile backdrop -->
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-150"
      leave-to-class="opacity-0"
    >
      <div
        v-if="mobileOpen"
        class="fixed inset-0 z-40 bg-slate-950/60 lg:hidden"
        aria-hidden="true"
        @click="mobileOpen = false"
      />
    </Transition>

    <!-- Sidebar -->
    <aside
      id="app-sidebar"
      class="fixed inset-y-0 start-0 z-50 flex w-64 flex-col bg-[#0F172A] text-slate-300 transition-[transform,width] duration-200 ease-out motion-reduce:transition-none lg:translate-x-0 lg:rtl:translate-x-0"
      :class="[mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full rtl:translate-x-full', collapsed ? 'lg:w-[4.25rem]' : 'lg:w-64']"
      :aria-label="t('nav.main')"
    >
      <div class="flex h-14 shrink-0 items-center gap-2 border-b border-white/5 px-4">
        <RouterLink
          to="/"
          class="flex items-center gap-2.5 text-white"
          :aria-label="collapsed ? t('nav.home') : undefined"
        >
          <span class="wordmark text-2xl" :class="collapsed ? 'lg:hidden' : ''">Displaro</span>
          <span v-if="collapsed" class="wordmark hidden text-2xl lg:inline">D</span>
        </RouterLink>
        <!-- Desktop: the bell beside the logo (the mobile header has its own). -->
        <NotificationBell v-if="!collapsed" class="ms-auto hidden lg:block" />
        <button
          type="button"
          class="ms-auto rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white lg:hidden"
          :aria-label="t('nav.close')"
          @click="mobileOpen = false"
        >
          <svg
            viewBox="0 0 20 20"
            class="h-5 w-5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            aria-hidden="true"
          >
            <path d="M5 5l10 10M15 5L5 15" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <!-- Organization switcher -->
      <div class="border-b border-white/5 px-3 py-3" :class="collapsed ? 'lg:hidden' : ''">
        <label
          class="mb-1 block px-1 text-[11px] font-medium tracking-wider text-slate-500 uppercase"
          :for="auth.organizations.length > 1 ? 'org-switcher' : undefined"
          >{{ t('nav.organization') }}</label
        >
        <!-- Nothing to switch to: just say which organization this is. -->
        <p v-if="org && auth.organizations.length < 2" class="truncate px-1 text-sm text-white" :title="org.name">
          {{ org.name }} <span class="text-slate-400">· {{ t(`roles.${org.role}`) }}</span>
        </p>
        <select
          v-else
          id="org-switcher"
          class="w-full rounded-md border border-white/10 bg-white/5 px-2.5 py-1.5 text-sm text-white outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30 disabled:opacity-70"
          :value="org?.id ?? ''"
          :disabled="switching"
          @change="onSwitch"
        >
          <option v-if="!org" value="" disabled>{{ t('nav.selectOrganization') }}</option>
          <option v-for="o in auth.organizations" :key="o.id" :value="o.id" class="text-slate-900">
            {{ o.name }} · {{ t(`roles.${o.role}`) }}
          </option>
        </select>
      </div>

      <nav class="flex-1 overflow-y-auto px-3 py-4">
        <div v-for="(section, si) in NAV" :key="si" class="mb-5">
          <p
            class="mb-1.5 px-2 text-[11px] font-medium tracking-wider text-slate-500 uppercase"
            :class="collapsed ? 'lg:sr-only' : ''"
          >
            {{ section.title }}
          </p>
          <ul class="space-y-0.5">
            <li v-for="item in section.items" :key="item.to.name">
              <RouterLink
                :to="item.to"
                class="group relative flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors"
                :class="[
                  isActive(item) ? 'bg-white/10 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white',
                  collapsed ? 'lg:justify-center' : '',
                ]"
                :aria-current="isActive(item) ? 'page' : undefined"
                :title="collapsed ? item.label : undefined"
              >
                <svg
                  viewBox="0 0 20 20"
                  class="h-5 w-5 shrink-0"
                  :class="isActive(item) ? 'text-indigo-300' : 'text-slate-500 group-hover:text-slate-300'"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linejoin="round"
                  stroke-linecap="round"
                  aria-hidden="true"
                >
                  <path :d="ICONS[item.icon]" />
                </svg>
                <span :class="collapsed ? 'lg:sr-only' : ''">{{ item.label }}</span>
                <svg
                  v-if="item.locked && !collapsed"
                  viewBox="0 0 16 16"
                  class="ms-auto h-3.5 w-3.5 shrink-0 text-amber-500"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  :aria-label="t('plan.lockedHint')"
                  role="img"
                ><title>{{ t('plan.lockedHint') }}</title><rect x="3" y="7" width="10" height="7" rx="1.5" /><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" /></svg>
                <span
                  v-if="item.badge"
                  class="ms-auto rounded-full px-1.5 text-[11px] font-semibold tabular-nums text-white"
                  :class="[item.badge.tone === 'critical' ? 'bg-red-500' : 'bg-amber-500', collapsed ? 'lg:absolute lg:end-1 lg:top-1 lg:ms-0' : '']"
                  :aria-label="t('nav.openIssues', item.badge.count)"
                  >{{ item.badge.count > 99 ? '99+' : item.badge.count }}</span
                >
              </RouterLink>
            </li>
          </ul>
        </div>
      </nav>

      <!-- Account -->
      <div class="border-t border-white/5 p-3">
        <div class="flex items-center gap-3" :class="collapsed ? 'lg:flex-col lg:gap-2' : ''">
          <UserAvatar :name="auth.user?.name ?? '?'" :url="auth.user?.avatarUrl" />
          <div class="min-w-0 flex-1" :class="collapsed ? 'lg:hidden' : ''">
            <RouterLink :to="{ name: 'organization-settings', query: { tab: 'account' } }" class="block truncate text-sm font-medium text-white hover:underline" :title="t('nav.accountSettings')">{{ auth.user?.name }}</RouterLink>
            <p class="truncate text-xs text-slate-500">{{ org ? t(`roles.${org.role}`) : t('nav.noOrganization') }}</p>
          </div>
          <ThemeSwitcher v-if="collapsed" class="hidden lg:block" variant="compact" tone="sidebar" />
          <button
            type="button"
            class="rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"
            :aria-label="t('common.signOut')"
            :title="t('common.signOut')"
            @click="signOut"
          >
            <svg
              viewBox="0 0 20 20"
              class="h-5 w-5"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M8 4H5v12h3M12 6.5 15.5 10 12 13.5M15.5 10H8" />
            </svg>
          </button>
        </div>
        <ThemeSwitcher class="mt-3" :class="collapsed ? 'lg:hidden' : ''" tone="sidebar" />
        <button
          type="button"
          class="mt-3 hidden w-full items-center justify-center gap-2 rounded-md py-1.5 text-xs text-slate-500 hover:bg-white/5 hover:text-slate-300 lg:flex"
          :aria-label="collapsed ? t('nav.expand') : t('nav.collapse')"
          :aria-expanded="!collapsed"
          @click="collapsed = !collapsed"
        >
          <svg
            viewBox="0 0 20 20"
            class="h-4 w-4 transition-transform"
            :class="collapsed ? 'rotate-180 rtl:rotate-0' : 'rtl:rotate-180'"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            aria-hidden="true"
          >
            <path d="M12 5l-5 5 5 5" />
          </svg>
          <span :class="collapsed ? 'hidden' : ''">{{ t('nav.collapseShort') }}</span>
        </button>
        <p class="mt-2 text-center text-[11px] font-medium text-slate-500" :class="collapsed ? 'lg:hidden' : ''">By HassbunaXAI</p><!-- i18n-ok: brand name -->
      </div>
    </aside>

    <!-- Content -->
    <div
      class="transition-[padding] duration-200 ease-out motion-reduce:transition-none"
      :class="collapsed ? 'lg:ps-[4.25rem]' : 'lg:ps-64'"
    >
      <SupportBanner />
      <div
        v-if="org && org.subscriptionStatus !== 'active'"
        class="border-b px-4 py-2 text-center text-sm"
        :class="
          org.subscriptionStatus === 'trial_expired' || org.subscriptionStatus === 'suspended'
            ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200'
            : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200'
        "
      >
        <template v-if="org.subscriptionStatus === 'trial_expired'">{{ t('nav.trialEnded') }}</template>
        <template v-else-if="org.subscriptionStatus === 'past_due' || org.subscriptionStatus === 'suspended'">{{ t(`billing.banner_${org.subscriptionStatus}`) }}</template>
        <template v-else>{{ t('nav.onTrial') }}</template>
        <RouterLink :to="{ name: 'organization-settings', query: { tab: 'subscription' } }" class="ms-1 font-medium underline">
          {{ org.subscriptionStatus === 'past_due' || org.subscriptionStatus === 'suspended' ? t('billing.fixPayment') : t('nav.seePlans') }}
        </RouterLink>
      </div>

      <main id="main" class="mx-auto max-w-[96rem] px-4 py-6 sm:px-6 lg:px-8">
        <p v-if="switchError" class="alert-error mb-4">{{ switchError }}</p>
        <slot />
      </main>
    </div>
    <ToastHost />
    <IssuePanelHost />
    <PublishIntentHost />
    <!-- Bottom corner: the sync tray above the uploads tray, over everything (sidebars and side panels too). -->
    <div class="fixed end-4 bottom-4 z-[60] flex flex-col items-end gap-3">
      <SyncTray />
      <UploadTray />
    </div>
  </div>
</template>
